import { isSedeMode } from "./partnerAccess/partnerStorage";

// ---------------------------------------------------------------------------
// The space the user last worked in, remembered across reloads and shared
// with the new app (`apps/next`).
//
// The entry is the new app's: its zustand `persist` store writes
// `{"state":{"activeSpaceId":"<id>"},"version":0}` under the key below (see
// `apps/next/src/modules/spaces/constants.ts` and
// `.../store/use-active-space-store.ts`). The host reads and writes that same
// entry in that same format, so both apps open in the same space. The key and
// the format live only here; `__tests__/activeSpaceStorage.contract.test.js`
// fails when the new app changes its side.
//
// Only the id is stored, and it is never trusted on its own: the caller keeps
// it only when the space is in the user's list.
//
// Inside a sede (partner access) the entry is neither read nor written: it
// belongs to the user's normal session, and a sede's spaces are another
// company's.
//
// Storage can be unavailable or throw on any access, and the entry can hold
// anything. Every failure here means "nothing remembered".
// ---------------------------------------------------------------------------

export const ACTIVE_SPACE_STORAGE_KEY = "dashboard-next-active-space";
export const ACTIVE_SPACE_STORAGE_VERSION = 0;

const isSpaceId = (value) => typeof value === "string" && value !== "";

export const readActiveSpaceId = () => {
  try {
    if (isSedeMode()) return null;
    const stored = window.localStorage.getItem(ACTIVE_SPACE_STORAGE_KEY);
    if (!stored) return null;
    const envelope = JSON.parse(stored);
    if (envelope?.version !== ACTIVE_SPACE_STORAGE_VERSION) return null;
    const spaceId = envelope.state?.activeSpaceId;
    return isSpaceId(spaceId) ? spaceId : null;
  } catch (e) {
    return null;
  }
};

/**
 * Remembers `spaceId` as the active space. Returns whether it was written:
 * anything that is not a real id is ignored, so the entry never ends up
 * holding `undefined` or `null` as text.
 */
export const writeActiveSpaceId = (spaceId) => {
  if (!isSpaceId(spaceId)) return false;
  try {
    if (isSedeMode()) return false;
    window.localStorage.setItem(
      ACTIVE_SPACE_STORAGE_KEY,
      JSON.stringify({ state: { activeSpaceId: spaceId }, version: ACTIVE_SPACE_STORAGE_VERSION })
    );
    return true;
  } catch (e) {
    return false;
  }
};
